/**
 * WebinarThankYou — köszönő oldal a webinar feliratkozás után.
 * Route: `/webinar-thankyou` (public, NEM Layout-wrappelt).
 *
 * Design: a `/webinar` (WebinarLanding) vizuális rendszerével konzisztens,
 * itt picit mélyebb lila/kék árnyalattal, kevesebb rózsaszínnel.
 * A felső sor (logo + dátum-pill + countdown) szándékosan duplikált, hogy
 * a WebinarLanding.js fájlhoz ne kelljen hozzányúlni.
 */
import React, { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { trackPageView, METAPIXEL_NOSCRIPT_URL } from "../lib/metaPixel";
import {
  Sparkle,
  Presentation,
  ChatCircleDots,
  Rocket,
  CalendarBlank,
  Bell,
  EnvelopeOpen,
} from "@phosphor-icons/react";

/* ════════════════════════════════════════════════════════════════════
 * Esemény-adatok — Google / Apple / Outlook linkekhez.
 * 2026. augusztus 13. csütörtök 19:00 Europe/Budapest (CEST = UTC+2) = 17:00 UTC.
 * Naptár időtartam: 90 perc (19:00–20:30 lokal, 17:00–18:30 UTC),
 * hogy legyen tér csúszásra és kérdésekre. A kommunikáció végig 60 perces.
 * ══════════════════════════════════════════════════════════════════ */
const EVENT = {
  title:
    "Ügyfélszerző anyagok pár kattintással, a saját hangodon – Ingyenes AI Marketing Mesterkurzus",
  description:
    "60 perces ingyenes AI marketing mesterkurzus Dr. Mózes Tamással. A meeting linket e-mailben küldjük.",
  url: "https://mex-foundation.emergent.host/webinar",
  // Compact UTC (naptár API-khoz)
  startUtc: "20260813T170000Z",
  endUtc:   "20260813T183000Z",
  // ISO (Outlook API-hoz)
  startIso: "2026-08-13T17:00:00Z",
  endIso:   "2026-08-13T18:30:00Z",
  // Lokal (ICS VTIMEZONE-hoz)
  startLocal: "20260813T190000",
  endLocal:   "20260813T203000",
  tzid: "Europe/Budapest",
};

function buildGoogleCalendarUrl() {
  const p = new URLSearchParams({
    action: "TEMPLATE",
    text: EVENT.title,
    dates: `${EVENT.startUtc}/${EVENT.endUtc}`,
    details: EVENT.description,
    location: EVENT.url,
    ctz: "Europe/Budapest",
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function buildOutlookCalendarUrl() {
  const p = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: EVENT.title,
    body: EVENT.description,
    startdt: EVENT.startIso,
    enddt: EVENT.endIso,
    location: EVENT.url,
    allday: "false",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`;
}

function buildIcsDataUrl() {
  // VTIMEZONE blokk Europe/Budapest-hez — RFC 5545 kompatibilis. Ez teszi
  // biztosítja, hogy Apple Calendar / iPhone / desktop naptárak ne csússzanak
  // el nyári/téli idő szerint (a webinar CEST = UTC+2 alatt van).
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//MEX Magic//Webinar//HU",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VTIMEZONE",
    `TZID:${EVENT.tzid}`,
    "BEGIN:DAYLIGHT",
    "TZOFFSETFROM:+0100",
    "TZOFFSETTO:+0200",
    "TZNAME:CEST",
    "DTSTART:19700329T020000",
    "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU",
    "END:DAYLIGHT",
    "BEGIN:STANDARD",
    "TZOFFSETFROM:+0200",
    "TZOFFSETTO:+0100",
    "TZNAME:CET",
    "DTSTART:19701025T030000",
    "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU",
    "END:STANDARD",
    "END:VTIMEZONE",
    "BEGIN:VEVENT",
    "UID:webinar-2026-08-13@mexmagic",
    `DTSTAMP:${EVENT.startUtc}`,
    `DTSTART;TZID=${EVENT.tzid}:${EVENT.startLocal}`,
    `DTEND;TZID=${EVENT.tzid}:${EVENT.endLocal}`,
    `SUMMARY:${EVENT.title}`,
    `DESCRIPTION:${EVENT.description}`,
    `URL:${EVENT.url}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

/* ════════════════════════════════════════════════════════════════════
 * Countdown — UGYANAZ mint a /webinar header-ben.
 * ══════════════════════════════════════════════════════════════════ */
const WEBINAR_TARGET_TS = Date.parse("2026-08-13T17:00:00Z");
function pad(n) { return String(n).padStart(2, "0"); }
function WebinarCountdown() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, WEBINAR_TARGET_TS - now);
  const days  = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins  = Math.floor((diff % 3600000) / 60000);
  const secs  = Math.floor((diff % 60000) / 1000);
  const parts = [
    [pad(days),  "nap"],
    [pad(hours), "óra"],
    [pad(mins),  "perc"],
    [pad(secs),  "mp"],
  ];
  return (
    <div
      data-testid="webinar-countdown"
      className="flex items-baseline gap-1.5"
      style={{ fontFamily: "inherit" }}
    >
      {parts.map(([v, label], i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center gap-0.5">
            <span
              className="font-heading"
              style={{
                fontSize: "clamp(22px, 1.8vw, 28px)",
                fontWeight: 300,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                color: "#1B1733",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {v}
            </span>
            <span
              style={{
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#6F6790",
                fontWeight: 500,
              }}
            >
              {label}
            </span>
          </div>
          {i < parts.length - 1 && (
            <span
              style={{
                color: "rgba(94,80,247,0.32)",
                fontSize: 15,
                fontWeight: 300,
                lineHeight: 1,
              }}
            >
              ·
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * TopBar — UGYANAZ mint a /webinar header (logo bal | pill közép | cd jobb).
 * ══════════════════════════════════════════════════════════════════ */
function WebinarTopBar() {
  return (
    <header
      data-testid="webinar-thankyou-header"
      className="w-full px-6 sm:px-10 lg:px-16 pt-6 pb-1 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3 md:gap-6 relative z-20"
    >
      {/* Bal: logó */}
      <a
        href="/webinar"
        className="flex items-center gap-3.5 justify-self-center md:justify-self-start"
        data-testid="webinar-thankyou-logo"
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-[0_12px_32px_rgba(94,80,247,0.36)]"
          style={{ background: "linear-gradient(135deg, #5e50f7 0%, #3a2fd3 100%)" }}
        >
          <Sparkle size={24} weight="fill" color="#fff" />
        </div>
        <div className="font-heading text-[24px] sm:text-[28px] tracking-tight text-[#2B2545] font-bold">
          MEX <span className="text-[#5e50f7]">Magic</span>
        </div>
      </a>

      {/* Közép: dátum-pill */}
      <div
        data-testid="webinar-thankyou-date-pill"
        className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full justify-self-center"
        style={{
          background: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(67,56,202,0.28)",
          color: "#2A2868",
          fontSize: "15px",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          boxShadow: "0 10px 24px -10px rgba(38,32,80,0.32)",
        }}
      >
        <span style={{ fontWeight: 600 }}>2026. augusztus 13. csütörtök, 19:00</span>
      </div>

      {/* Jobb: countdown */}
      <div className="justify-self-center md:justify-self-end">
        <WebinarCountdown />
      </div>
    </header>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * Háttér — mélyebb lila/kék, kevesebb rózsaszín, határozottabb kontraszt.
 * ══════════════════════════════════════════════════════════════════ */
function BackgroundLayer() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(160deg, #E7EAF1 0%, #D6DAE6 28%, #C7CCDE 55%, #CDD2E2 78%, #D8DCE9 100%)",
        }}
      />
      {/* Finom rács */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(48,46,102,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(48,46,102,0.06) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 60%, rgba(0,0,0,0) 95%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 0%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 60%, rgba(0,0,0,0) 95%)",
        }}
      />
      {/* Indigó glow blob — jobb felső */}
      <div
        className="absolute"
        style={{
          top: "-12%",
          right: "-8%",
          width: "55%",
          height: "72%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(67,56,202,0.42) 0%, rgba(79,70,229,0.16) 42%, rgba(79,70,229,0) 72%)",
          filter: "blur(22px)",
        }}
      />
      {/* Hűvös szteel-kék — bal felső */}
      <div
        className="absolute"
        style={{
          top: "18%",
          left: "-10%",
          width: "42%",
          height: "55%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(56,90,160,0.20) 0%, rgba(56,90,160,0.08) 45%, rgba(56,90,160,0) 75%)",
          filter: "blur(28px)",
        }}
      />
      {/* Mély indigó — alsó középső */}
      <div
        className="absolute"
        style={{
          bottom: "-15%",
          left: "20%",
          width: "55%",
          height: "50%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(49,46,129,0.22) 0%, rgba(49,46,129,0.08) 42%, rgba(49,46,129,0) 72%)",
          filter: "blur(30px)",
        }}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * ExpectItem — vízszintes mini-blokk: [ikon | cím + rövid leírás]
 * 3 oszlopos grid desktopon, egymás alá mobilon.
 * ══════════════════════════════════════════════════════════════════ */
function ExpectItem({ icon: Icon, title, text, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-row items-start gap-5"
    >
      <div
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background:
            "linear-gradient(135deg, rgba(94,80,247,0.24) 0%, rgba(67,56,202,0.16) 100%)",
          border: "1px solid rgba(67,56,202,0.42)",
          boxShadow:
            "0 14px 32px -12px rgba(38,32,80,0.45), inset 0 1px 0 rgba(255,255,255,0.75)",
        }}
      >
        <Icon size={32} weight="duotone" color="#4F46E5" />
      </div>
      <div className="min-w-0">
        <h3
          className="font-heading mb-2"
          style={{
            fontSize: 19,
            fontWeight: 700,
            color: "#1B1733",
            letterSpacing: "-0.015em",
            lineHeight: 1.25,
          }}
        >
          {title}
        </h3>
        <p style={{ fontSize: 15, lineHeight: 1.6, color: "#3F365E" }}>
          {text}
        </p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * StepCard — „Következő lépések" glass kártya
 * ══════════════════════════════════════════════════════════════════ */
function StepCard({ title, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl"
      style={{
        background: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(255,255,255,0.95)",
        boxShadow:
          "0 28px 70px -22px rgba(38,32,80,0.32), inset 0 1px 0 rgba(255,255,255,0.9)",
        padding: "24px 24px 22px",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      <h3
        className="font-heading mb-3"
        style={{
          fontSize: 17,
          fontWeight: 700,
          color: "#1B1733",
          letterSpacing: "-0.01em",
          lineHeight: 1.3,
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * Felhasználó által feltöltött host fotó (placeholder).
 * Később ki kell cserélni a végleges portréra.
 * ══════════════════════════════════════════════════════════════════ */
const WEBINAR_HOST_PHOTO =
  "https://customer-assets.emergentagent.com/job_mex-foundation/artifacts/geuem7zj_brandagency-render%20%2845%29.png";

/* Pre-computed URL-ek (konstansok, mert az esemény fix). */
const GOOGLE_URL = buildGoogleCalendarUrl();
const OUTLOOK_URL = buildOutlookCalendarUrl();
const ICS_URL = buildIcsDataUrl();

/* ════════════════════════════════════════════════════════════════════
 * FŐKOMPONENS
 * ══════════════════════════════════════════════════════════════════ */
export default function WebinarThankYou() {
  // Meta Pixel — alap PageView a Thank You Page-en. Egyéni konverziót
  // a Meta Events Managerben lehet ezen URL-re állítani (nem Lead event).
  useEffect(() => {
    trackPageView("/webinar-thank-you");
  }, []);

  // ═══ NAGY KONFETTI ÁGYÚ ═══════════════════════════════════════════
  // Két-oldali cannon burst a Thank You Page megérkezésekor. Egyszer
  // fut le success-per-session (sessionStorage flag), tiszteletben tartja
  // a prefers-reduced-motion beállítást. Fixed layer, pointer-events: none.
  useEffect(() => {
    try {
      const reduced =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) return;

      const KEY = "webinar_thankyou_confetti_v1";
      if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(KEY)) return;
      if (typeof sessionStorage !== "undefined") sessionStorage.setItem(KEY, "1");

      const colors = ["#5e50f7", "#7c3aed", "#a78bfa", "#c4b5fd", "#f0abfc", "#22d3ee", "#fbbf24", "#f472b6"];

      // A canvas-confetti default `confetti()` hívása egy globális
      // canvas-t hoz létre fixed layerben, pointer-events: none-nal.
      // Ez layout-safe és nem okoz scrollbart.

      // ═══ KEZDŐ DUPLA BURST — bal + jobb alsó cannon ═══
      // Finomhangolva: kisebb robbanás, gyorsabb esés, nagyobb darabok.
      confetti({
        particleCount: 180,
        angle: 60,
        spread: 85,
        origin: { x: 0.0, y: 0.95 },
        colors,
        startVelocity: 55,
        scalar: 1.35,
        gravity: 1.3,
        ticks: 220,
      });
      confetti({
        particleCount: 180,
        angle: 120,
        spread: 85,
        origin: { x: 1.0, y: 0.95 },
        colors,
        startVelocity: 55,
        scalar: 1.35,
        gravity: 1.3,
        ticks: 220,
      });

      // Kis középső sky-burst — most kisebb, hogy ne legyen túlzó
      confetti({
        particleCount: 60,
        angle: 90,
        spread: 120,
        origin: { x: 0.5, y: 0.75 },
        colors,
        startVelocity: 42,
        scalar: 1.3,
        gravity: 1.3,
        ticks: 220,
      });

      // ═══ Follow-up burst-ök 1.4 másodpercig ═══
      const duration = 1400;
      const end = Date.now() + duration;
      const timer = setInterval(() => {
        if (Date.now() > end) {
          clearInterval(timer);
          return;
        }
        confetti({
          particleCount: 30,
          angle: 60,
          spread: 65,
          origin: { x: Math.random() * 0.3, y: 0.9 },
          colors,
          startVelocity: 45,
          scalar: 1.3,
          gravity: 1.3,
        });
        confetti({
          particleCount: 30,
          angle: 120,
          spread: 65,
          origin: { x: 0.7 + Math.random() * 0.3, y: 0.9 },
          colors,
          startVelocity: 45,
          scalar: 1.3,
          gravity: 1.3,
        });
      }, 220);

      return () => {
        clearInterval(timer);
      };
    } catch {
      /* silent — konfetti nem kritikus */
    }
  }, []);

  return (
    <div
      data-testid="webinar-thankyou-page"
      className="font-sans relative overflow-x-hidden min-h-screen flex flex-col"
      data-font="geist"
      style={{
        background:
          "radial-gradient(60% 50% at 78% 16%, rgba(67,56,202,0.18) 0%, rgba(255,255,255,0) 60%), radial-gradient(50% 40% at 16% 30%, rgba(56,118,180,0.10) 0%, rgba(255,255,255,0) 65%), linear-gradient(180deg, #E5E8F2 0%, #D5D9E8 100%)",
      }}
    >
      <BackgroundLayer />

      <div className="relative z-10 flex-1 flex flex-col">
        <WebinarTopBar />

        <main
          data-testid="webinar-thankyou-main"
          className="flex-1 px-6 sm:px-10 lg:px-16 pt-8 lg:pt-12 pb-20 lg:pb-28"
        >
          <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-[55%_45%] gap-10 lg:gap-14 items-start">
            {/* ─── BAL OSZLOP (~55%) ─────────────────────────────────── */}
            <div data-testid="webinar-thankyou-left">
              <h1
                className="font-heading"
                data-testid="webinar-thankyou-headline"
                style={{
                  fontSize: "clamp(28px, 3.2vw, 42px)",
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: "-0.03em",
                  color: "#1B1733",
                }}
              >
                Köszönöm, sikeresen regisztráltál!{" "}
                <span style={{ display: "inline-block" }}>🎉</span>
              </h1>

              <p
                data-testid="webinar-thankyou-subtitle"
                className="mt-4 max-w-[600px]"
                style={{
                  fontSize: "clamp(15px, 1.05vw, 17px)",
                  lineHeight: 1.65,
                  color: "#3F365E",
                }}
              >
                A webinár részleteit hamarosan elküldjük e-mailben. Addig is mentsd el az időpontot, és nézd át, mire számíthatsz.
              </p>

              {/* Host fotó — placeholder a feltöltött képpel */}
              <div className="mt-8 lg:mt-10 relative" data-testid="webinar-thankyou-photo-wrap">
                <div
                  aria-hidden="true"
                  className="absolute -inset-3 rounded-[28px]"
                  style={{
                    background:
                      "radial-gradient(60% 60% at 50% 50%, rgba(79,70,229,0.28) 0%, rgba(76,29,149,0.12) 50%, rgba(94,80,247,0) 80%)",
                    filter: "blur(20px)",
                    zIndex: 0,
                  }}
                />
                <div
                  className="relative rounded-[20px] overflow-hidden"
                  style={{
                    aspectRatio: "16 / 9",
                    background: "#1B1733",
                    border: "1px solid rgba(255,255,255,0.85)",
                    boxShadow:
                      "0 36px 90px -22px rgba(40,32,90,0.45), inset 0 1px 0 rgba(255,255,255,0.65)",
                    zIndex: 1,
                  }}
                  data-testid="webinar-thankyou-photo"
                >
                  <img
                    src={WEBINAR_HOST_PHOTO}
                    alt="Webinar host portré (placeholder)"
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                      display: "block",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ─── JOBB OSZLOP (~45%) ────────────────────────────────── */}
            <aside data-testid="webinar-thankyou-right" className="lg:pl-2">
              <h2
                className="font-heading mb-7"
                data-testid="webinar-thankyou-steps-heading"
                style={{
                  fontSize: "clamp(22px, 2vw, 28px)",
                  fontWeight: 700,
                  color: "#1B1733",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                Következő lépések
              </h2>

              <div className="flex flex-col gap-5">
                {/* Kártya 1: Naptár — Google + Apple + Outlook */}
                <StepCard
                  testid="webinar-thankyou-step-1"
                  title="1. Add hozzá a naptáradhoz"
                >
                  <p
                    style={{
                      fontSize: 14.5,
                      lineHeight: 1.6,
                      color: "#3F365E",
                      marginBottom: 16,
                    }}
                  >
                    Foglalj helyet neki most a naptáradban, hogy biztosan ne maradj le.
                  </p>
                  <div className="flex flex-wrap gap-2.5">
                    <a
                      href={GOOGLE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="webinar-thankyou-cal-google"
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg"
                      style={{
                        background: "linear-gradient(135deg, #5e50f7 0%, #7c3aed 100%)",
                        border: "1px solid rgba(94,80,247,0.55)",
                        color: "#FFFFFF",
                        fontSize: 13.5,
                        fontWeight: 700,
                        boxShadow: "0 10px 22px -8px rgba(94,80,247,0.55), 0 4px 10px -4px rgba(124,58,237,0.45)",
                      }}
                    >
                      <CalendarBlank size={15} weight="duotone" color="#FFFFFF" />
                      Google naptár
                    </a>
                    <a
                      href={ICS_URL}
                      download="mex-magic-webinar.ics"
                      data-testid="webinar-thankyou-cal-apple"
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg"
                      style={{
                        background: "linear-gradient(135deg, #5e50f7 0%, #7c3aed 100%)",
                        border: "1px solid rgba(94,80,247,0.55)",
                        color: "#FFFFFF",
                        fontSize: 13.5,
                        fontWeight: 700,
                        boxShadow: "0 10px 22px -8px rgba(94,80,247,0.55), 0 4px 10px -4px rgba(124,58,237,0.45)",
                      }}
                    >
                      <CalendarBlank size={15} weight="duotone" color="#FFFFFF" />
                      Apple naptár
                    </a>
                    <a
                      href={OUTLOOK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="webinar-thankyou-cal-outlook"
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg"
                      style={{
                        background: "linear-gradient(135deg, #5e50f7 0%, #7c3aed 100%)",
                        border: "1px solid rgba(94,80,247,0.55)",
                        color: "#FFFFFF",
                        fontSize: 13.5,
                        fontWeight: 700,
                        boxShadow: "0 10px 22px -8px rgba(94,80,247,0.55), 0 4px 10px -4px rgba(124,58,237,0.45)",
                      }}
                    >
                      <CalendarBlank size={15} weight="duotone" color="#FFFFFF" />
                      Outlook
                    </a>
                  </div>
                </StepCard>

                {/* Kártya 2: Gyere el élőben — erősített szöveg */}
                <StepCard
                  testid="webinar-thankyou-step-2"
                  title="2. Gyere el élőben"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="inline-flex items-center justify-center shrink-0"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background:
                          "linear-gradient(135deg, rgba(94,80,247,0.20) 0%, rgba(76,29,149,0.12) 100%)",
                        border: "1px solid rgba(94,80,247,0.36)",
                      }}
                    >
                      <Bell size={18} weight="duotone" color="#4F46E5" />
                    </div>
                    <p
                      style={{
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        color: "#3F365E",
                      }}
                    >
                      Az előadásról nem lesz visszanézhető felvétel, ezért érdemes élőben ott lenned. Ráadásul az élő résztvevők között ajándékot is kisorsolunk.
                    </p>
                  </div>
                </StepCard>

                {/* Kártya 3: E-mailek */}
                <StepCard
                  testid="webinar-thankyou-step-3"
                  title="3. Nézd meg az e-mailjeidet"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="inline-flex items-center justify-center shrink-0"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background:
                          "linear-gradient(135deg, rgba(94,80,247,0.20) 0%, rgba(76,29,149,0.12) 100%)",
                        border: "1px solid rgba(94,80,247,0.36)",
                      }}
                    >
                      <EnvelopeOpen size={18} weight="duotone" color="#4F46E5" />
                    </div>
                    <p
                      style={{
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        color: "#3F365E",
                      }}
                    >
                      Pár percen belül elküldjük a részleteket. Ha nem találod, nézd meg a Promóciók vagy Spam mappát is.
                    </p>
                  </div>
                </StepCard>
              </div>
            </aside>
          </div>

          {/* ─── „MIRE SZÁMÍTHATSZ" — TELJES SZÉLESSÉGŰ 3 OSZLOPOS GRID ─── */}
          <section
            data-testid="webinar-thankyou-expect-section"
            className="max-w-[1280px] mx-auto mt-10 lg:mt-14"
          >
            <h2
              data-testid="webinar-thankyou-expect-heading"
              className="font-heading mb-8 lg:mb-10"
              style={{
                fontSize: "clamp(22px, 2vw, 28px)",
                fontWeight: 700,
                color: "#1B1733",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Mire számíthatsz
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 40,
                width: "100%",
              }}
              className="webinar-thankyou-expect-grid"
            >
              <ExpectItem
                testid="webinar-thankyou-expect-1"
                icon={Presentation}
                title="Élő előadás"
                text="60 perc gyakorlatias AI-nézőpontváltás, hogy tisztábban lásd, hogyan készülhetnek ügyfélszerző anyagok a saját hangodon."
              />
              <ExpectItem
                testid="webinar-thankyou-expect-2"
                icon={ChatCircleDots}
                title="Kérdések és válaszok"
                text="Élőben kérdezhetsz, és választ kapsz arra, hogyan működik mindez a saját helyzetedben."
              />
              <ExpectItem
                testid="webinar-thankyou-expect-3"
                icon={Rocket}
                title="Praktikus ötletek"
                text="Konkrét ötletekkel és tisztább iránnyal mész tovább, amit rögtön be tudsz építeni a marketingedbe."
              />
            </div>
            <style>{`
              @media (max-width: 768px) {
                .webinar-thankyou-expect-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
          </section>
        </main>

        {/* Lábléc — jogi link sor + Meta disclaimer */}
        <footer
          data-testid="webinar-thankyou-footer"
          className="px-6 sm:px-10 lg:px-16 py-7"
          style={{
            background: "#D2D6E4",
            borderTop: "1px solid rgba(48,46,102,0.12)",
            color: "#52587A",
          }}
        >
          <div className="max-w-[1280px] mx-auto flex flex-col gap-3 items-center text-center">
            {/* Jogi link sor */}
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1" style={{ fontSize: 12.5 }}>
              <a href="/aszf" data-testid="footer-link-aszf" className="hover:text-[#4F46E5] transition-colors">
                ÁSZF
              </a>
              <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
              <span>MEX Magic • All rights reserved.</span>
              <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
              <a href="/adatkezelesi-tajekoztato" data-testid="footer-link-gdpr" className="hover:text-[#4F46E5] transition-colors">
                Adatkezelési tájékoztató
              </a>
            </div>
            {/* Meta disclaimer (megmarad) — 2 sorban, kiegyensúlyozott törés */}
            <div
              className="mx-auto text-center"
              style={{ fontSize: 11.5, lineHeight: 1.55, maxWidth: 720, color: "#7B7596" }}
            >
              <div>
                Ez az oldal nem része a Facebooknak vagy a Meta Platforms Inc.-nek, és nem áll velük kapcsolatban.
              </div>
              <div>
                A Facebook a Meta Platforms Inc. védjegye.
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Meta Pixel — noscript fallback PageView */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={METAPIXEL_NOSCRIPT_URL}
        />
      </noscript>
    </div>
  );
}
